import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, FileText, Loader2, CheckCircle2, XCircle, Quote, RotateCcw } from "lucide-react";

// Interaktivt övningsprov: AI-genererade frågor STRIKT förankrade i händelsens
// material. Fråga-för-fråga med server-side rättning (facit skickas aldrig i förväg).
export default function PracticeExamModal({ eventId, eventTitle, onClose }) {
  const [exam, setExam] = useState(null); // {exam_id, total, questions:[{idx,type,question,options?}]}
  const [idx, setIdx] = useState(0);
  const [openAnswer, setOpenAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // svar på senaste frågan
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [resumed, setResumed] = useState(false);
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/practice-exam/history`);
      setHistory(data.rounds || []);
    } catch { /* historik är nice-to-have */ }
  };

  const generate = async (force = false) => {
    setLoading(true); setError(""); setExam(null); setSummary(null);
    setFeedback(null); setIdx(0); setOpenAnswer(""); setPicked(null); setElapsed(0);
    setResumed(false);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    try {
      if (!force) {
        // Fortsätt senare: återuppta påbörjat prov om ett finns
        try {
          const { data: act } = await api.get(`/events/${eventId}/practice-exam/active`, { timeout: 20_000 });
          if (act.active && act.idx < act.total) {
            setExam(act);
            setIdx(act.idx);
            setResumed(act.idx > 0);
            return;
          }
        } catch { /* ingen aktiv — generera nytt */ }
      }
      // Async-generering: POST startar, sedan pollas status var 3:e sekund.
      // Undviker proxy/Cloudflare-timeouts på långa LLM-anrop.
      const { data: start } = await api.post(`/events/${eventId}/practice-exam`, {}, { timeout: 30_000 });
      const examId = start.exam_id;
      const deadline = Date.now() + 300_000;
      let ready = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        let st;
        try {
          ({ data: st } = await api.get(`/practice-exams/${examId}`, { timeout: 20_000 }));
        } catch { continue; /* tillfälligt nätfel — polla vidare */ }
        if (st.status === "error") throw new Error(st.error || "Genereringen misslyckades.");
        if (st.status === "active" || st.status === "done") { ready = st; break; }
      }
      if (!ready) throw new Error("Genereringen tog för lång tid. Försök igen.");
      setExam(ready);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Kunde inte skapa övningsprovet. Försök igen.");
    } finally {
      clearInterval(timerRef.current);
      setLoading(false);
    }
  };

  const startNew = async () => {
    if (exam?.exam_id && !summary) {
      try { await api.post(`/practice-exams/${exam.exam_id}/abandon`); } catch { /* ok */ }
    }
    generate(true);
  };

  useEffect(() => {
    generate();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const submit = async (answer) => {
    if (grading) return;
    setGrading(true);
    try {
      const { data } = await api.post(`/practice-exams/${exam.exam_id}/answer`, {
        q_idx: idx, answer: String(answer),
      }, { timeout: 90_000 });
      setFeedback(data);
      if (data.done) { setSummary(data.summary); loadHistory(); }
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunde inte rätta svaret.");
    } finally {
      setGrading(false);
    }
  };

  const next = () => { setFeedback(null); setOpenAnswer(""); setPicked(null); setIdx((i) => i + 1); };

  const q = exam?.questions?.[idx];

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={(e) => e.stopPropagation()}
         data-testid="practice-exam-modal">
      <div className="bg-[color:var(--bg,#FAF9F6)] w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[color:var(--jarvis)] text-white flex items-center justify-center flex-shrink-0">
              <FileText size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Övningsprov</p>
              <p className="text-xs text-[color:var(--text-tertiary)] truncate">{eventTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-black/5" data-testid="practice-exam-close">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="py-12 text-center space-y-3" data-testid="practice-exam-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--jarvis)]" />
            <p className="text-sm text-[color:var(--text-secondary)]">
              Skriver frågor ur ditt material… {elapsed > 5 ? `(${elapsed}s)` : ""}
            </p>
            <p className="text-xs text-[color:var(--text-tertiary)]">
              Varje fråga beläggs med citat ur materialet — inga påhittade fakta.
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="py-8 text-center space-y-3" data-testid="practice-exam-error">
            <p className="text-sm text-[#B0563E]">{error}</p>
            <button onClick={() => generate(true)}
              className="text-xs px-3 py-1.5 rounded-full bg-[color:var(--jarvis)] text-white font-medium"
              data-testid="practice-exam-retry">
              Försök igen
            </button>
          </div>
        )}

        {!loading && !error && summary && (
          <div className="space-y-4" data-testid="practice-exam-summary">
            <div className="text-center py-3">
              <p className="text-3xl font-bold">{summary.correct}/{summary.total}</p>
              <p className="text-sm text-[color:var(--text-secondary)] mt-1">
                {summary.correct === summary.total ? "Perfekt! 🎉"
                  : summary.correct >= summary.total * 0.7 ? "Bra jobbat! 💪"
                  : "Bra kämpat — repetera de röda och kör igen!"}
              </p>
            </div>
            {summary.wrong?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
                  Att repetera ({summary.wrong.length})
                </p>
                {summary.wrong.map((w, i) => (
                  <div key={i} className="text-xs bg-white border border-[color:var(--border)] rounded-lg p-2.5">
                    <p className="font-medium">{w.q}</p>
                    <p className="text-[color:var(--moss)] mt-1">Facit: {w.a}</p>
                  </div>
                ))}
              </div>
            )}
            {history.length > 1 && (
              <p className="text-xs text-center text-[color:var(--text-secondary)]" data-testid="practice-exam-trend">
                Dina prov: {history.map((h) => `${h.correct}/${h.total}`).join(" → ")}
                {history[history.length - 1].correct > history[0].correct ? " 📈" : ""}
              </p>
            )}
            <div className="flex gap-2">
              <button onClick={() => generate(true)}
                className="flex-1 text-sm px-3 py-2.5 rounded-xl bg-[color:var(--jarvis)] text-white font-medium flex items-center justify-center gap-1.5"
                data-testid="practice-exam-new">
                <RotateCcw size={14} /> Gör ett nytt prov
              </button>
              <button onClick={onClose}
                className="px-4 py-2.5 rounded-xl border border-[color:var(--border)] text-sm font-medium"
                data-testid="practice-exam-done-close">
                Stäng
              </button>
            </div>
          </div>
        )}

        {!loading && !error && !summary && q && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-xs text-[color:var(--text-tertiary)]">
              <span data-testid="practice-exam-progress">Fråga {idx + 1} av {exam.total}</span>
              <span className="flex items-center gap-2">
                {q.type === "mc" ? "Flerval" : "Egen förklaring"}
                <button onClick={startNew} className="underline underline-offset-2 hover:text-[color:var(--jarvis)]"
                        data-testid="practice-exam-start-new">
                  Starta nytt
                </button>
              </span>
            </div>
            {resumed && idx === exam.idx && !feedback && (
              <p className="text-xs px-3 py-2 rounded-lg bg-[#EEF2F7] text-[#3D5A80]" data-testid="practice-exam-resumed-note">
                Fortsätter där du var — {exam.correct} rätt hittills.
              </p>
            )}
            <div className="h-1 rounded-full bg-black/5 overflow-hidden">
              <div className="h-full bg-[color:var(--jarvis)] transition-all"
                   style={{ width: `${((idx + (feedback ? 1 : 0)) / exam.total) * 100}%` }} />
            </div>

            <p className="text-sm font-medium leading-relaxed" data-testid="practice-exam-question">
              {q.question}
            </p>

            {q.type === "mc" ? (
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  let cls = "bg-white border-[color:var(--border)] hover:border-[color:var(--jarvis)]";
                  if (feedback) {
                    if (oi === feedback.correct_index) cls = "bg-[#F0F6E6] border-[color:var(--moss)] text-[color:var(--moss)] font-medium";
                    else if (oi === picked && !feedback.correct) cls = "bg-[#FDF2EE] border-[#E07A5F] text-[#B0563E]";
                    else cls = "bg-white border-[color:var(--border)] opacity-60";
                  } else if (oi === picked) {
                    cls = "bg-white border-[color:var(--jarvis)]";
                  }
                  return (
                    <button key={oi} disabled={!!feedback || grading}
                      onClick={() => { setPicked(oi); submit(oi); }}
                      className={`w-full text-left text-sm px-3 py-2.5 rounded-xl border transition-colors ${cls}`}
                      data-testid={`practice-exam-option-${oi}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  value={openAnswer}
                  onChange={(e) => setOpenAnswer(e.target.value)}
                  disabled={!!feedback || grading}
                  rows={3}
                  placeholder="Skriv ditt svar med egna ord…"
                  className="w-full text-sm rounded-xl border border-[color:var(--border)] p-3 bg-white focus:outline-none focus:border-[color:var(--jarvis)]"
                  data-testid="practice-exam-open-input"
                />
                {!feedback && (
                  <button onClick={() => submit(openAnswer)}
                    disabled={grading || openAnswer.trim().length < 2}
                    className="w-full text-sm px-3 py-2.5 rounded-xl bg-[color:var(--jarvis)] text-white font-medium disabled:opacity-50 flex items-center justify-center gap-1.5"
                    data-testid="practice-exam-submit-open">
                    {grading ? <Loader2 size={14} className="animate-spin" /> : null}
                    {grading ? "Rättar…" : "Svara"}
                  </button>
                )}
              </div>
            )}

            {grading && q.type === "mc" && (
              <p className="text-xs text-[color:var(--text-tertiary)] flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> Rättar…
              </p>
            )}

            {feedback && (
              <div className={`rounded-xl p-3 space-y-2 ${feedback.correct ? "bg-[#F0F6E6]" : "bg-[#FDF2EE]"}`}
                   data-testid="practice-exam-feedback">
                <p className={`text-sm font-semibold flex items-center gap-1.5 ${feedback.correct ? "text-[color:var(--moss)]" : "text-[#B0563E]"}`}>
                  {feedback.correct ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                  {feedback.correct ? "Rätt!" : "Inte riktigt"}
                  {feedback.feedback ? <span className="font-normal">— {feedback.feedback}</span> : null}
                </p>
                {!feedback.correct && (
                  <p className="text-xs"><span className="font-medium">Facit:</span> {feedback.facit}</p>
                )}
                {feedback.source_quote && (
                  <p className="text-xs text-[color:var(--text-secondary)] flex items-start gap-1.5">
                    <Quote size={11} className="mt-0.5 flex-shrink-0" />
                    <span className="italic">"{feedback.source_quote}"</span>
                  </p>
                )}
                {!feedback.done && (
                  <button onClick={next}
                    className="w-full text-sm px-3 py-2 rounded-xl bg-[color:var(--jarvis)] text-white font-medium mt-1"
                    data-testid="practice-exam-next">
                    Nästa fråga
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
